import type { IconName } from "@/components/ui/Icon";
import { NAV_ITEMS, visibleNavItems, type NavGroup, type NavItem } from "./navigation";

/**
 * Navigatsiya DARAXTI.
 *
 * Uch daraja:
 *
 *   1. BO'LIM — chap chetdagi qora relsdagi belgi (Asosiy,
 *      Operatsiyalar, Ombor, Hisobotlar, Sozlamalar);
 *   2. GURUH — yon menyudagi kichik kulrang sarlavha
 *      (masalan «Kirim», «Chiqim», «Nazorat»);
 *   3. MODUL — menyu qatori; uning ostida bolalar daraxt
 *      chizig'i bilan (masalan «Topshiriqlar» → «Tarix»).
 *
 * Joylashuv `navigation.ts` dagi `NAV_ITEMS` dan ALOHIDA
 * saqlanadi: modul ro'yxatiga yangi sahifa qo'shilganda (boshqa
 * jamoa a'zosi ham qo'shishi mumkin) bu fayl tegilmasa ham u
 * menyuda chiqadi — `group` maydoni bo'yicha tegishli bo'limning
 * «Boshqa» guruhiga tushadi. Yo'qolib qolmaydi.
 */
export type SectionId = "home" | "operations" | "warehouse" | "analytics" | "system";

export interface NavSection {
  id: SectionId;
  label: string;
  icon: IconName;
  /** Guruhlar — yon menyuda shu tartibda. */
  groups: Array<{ id: string; label: string }>;
}

export const NAV_SECTIONS: NavSection[] = [
  {
    id: "home",
    label: "Asosiy",
    icon: "home",
    groups: [
      { id: "overview", label: "Umumiy ko'rinish" },
      { id: "signals", label: "Xabarlar" },
    ],
  },
  {
    id: "operations",
    label: "Operatsiyalar",
    icon: "task",
    groups: [
      { id: "work", label: "Ish navbati" },
      { id: "inbound", label: "Kirim" },
      { id: "outbound", label: "Chiqim" },
      { id: "control", label: "Nazorat" },
      { id: "other", label: "Boshqa" },
    ],
  },
  {
    id: "warehouse",
    label: "Ombor",
    icon: "warehouse",
    groups: [
      { id: "space", label: "Joy" },
      { id: "goods", label: "Tovar va qoldiq" },
      { id: "other", label: "Boshqa" },
    ],
  },
  {
    id: "analytics",
    label: "Hisobotlar",
    icon: "report",
    groups: [
      { id: "reports", label: "Tahlil" },
      { id: "other", label: "Boshqa" },
    ],
  },
  {
    id: "system",
    label: "Sozlamalar",
    icon: "settings",
    groups: [
      { id: "integration", label: "1C va ma'lumot" },
      { id: "config", label: "Tizim" },
      { id: "access", label: "Kirish va iz" },
      { id: "other", label: "Boshqa" },
    ],
  },
];

interface Placement {
  section: SectionId;
  group: string;
  /** Ota modul yo'li — shu modul uning ostida daraxt bo'lib chiqadi. */
  parent?: string;
}

/** Mavjud modullarning joyi. Bu yerda yo'q modul `group` bo'yicha joylanadi. */
const PLACEMENT: Record<string, Placement> = {
  "/": { section: "home", group: "overview" },
  "/map": { section: "home", group: "overview" },
  "/map-3d": { section: "home", group: "overview", parent: "/map" },
  "/notifications": { section: "home", group: "signals" },

  "/tasks": { section: "operations", group: "work" },
  "/task-history": { section: "operations", group: "work", parent: "/tasks" },
  "/receiving-sessions": { section: "operations", group: "inbound" },
  "/return-sessions": { section: "operations", group: "inbound", parent: "/receiving-sessions" },
  "/orders": { section: "operations", group: "outbound" },
  "/decisions": { section: "operations", group: "control" },
  "/discrepancies": { section: "operations", group: "control" },
  "/counts": { section: "operations", group: "control" },
  "/blocks": { section: "operations", group: "control" },

  "/topology": { section: "warehouse", group: "space" },
  "/pallets": { section: "warehouse", group: "space" },
  "/journal": { section: "warehouse", group: "space" },
  "/skus": { section: "warehouse", group: "goods" },
  "/stock": { section: "warehouse", group: "goods" },

  "/reports": { section: "analytics", group: "reports" },
  "/abc-xyz": { section: "analytics", group: "reports", parent: "/reports" },
  "/quality": { section: "analytics", group: "reports", parent: "/reports" },

  "/integration": { section: "system", group: "integration" },
  "/reconciliation": { section: "system", group: "integration", parent: "/integration" },
  "/imports": { section: "system", group: "integration" },
  "/settings": { section: "system", group: "config" },
  "/backup": { section: "system", group: "config" },
  "/security": { section: "system", group: "access" },
  "/audit": { section: "system", group: "access" },
};

/** Eski guruh → bo'lim. Tanilmagan guruh oxirgi bo'limga tushadi. */
const GROUP_FALLBACK: Record<NavGroup, SectionId> = {
  warehouse: "warehouse",
  operations: "operations",
  analytics: "analytics",
  system: "system",
};

export function placementOf(item: NavItem): Placement {
  const known = PLACEMENT[item.path];
  if (known) return known;
  // Yangi modul: `/map` ostidagi yo'l (masalan 3D ko'rinish)
  // xaritaning bolasi bo'ladi — ular bir savolga javob beradi.
  const parent = Object.keys(PLACEMENT)
    .filter((path) => path !== "/" && item.path.startsWith(`${path}/`))
    .sort((a, b) => b.length - a.length)[0];
  if (parent) return { ...PLACEMENT[parent]!, parent };
  const section = GROUP_FALLBACK[item.group as NavGroup] ?? "system";
  return { section, group: "other" };
}

export interface NavNode {
  item: NavItem;
  children: NavItem[];
}

export interface NavTreeGroup {
  id: string;
  label: string;
  nodes: NavNode[];
}

export interface NavTreeSection extends NavSection {
  tree: NavTreeGroup[];
  /** Bo'limdagi barcha modullar (bolalar ham). */
  items: NavItem[];
}

/**
 * Ko'rinadigan modullardan daraxt quradi.
 *
 * Vakolat bo'yicha ko'rinmaydigan modul daraxtga TUSHMAYDI; ota
 * modul ko'rinmasa, bolasi o'zi yuqori darajaga chiqadi — aks
 * holda xodim ruxsati bor sahifaga yo'l topolmasdi.
 */
export function navTree(permissions: string[]): NavTreeSection[] {
  const visible = visibleNavItems(permissions);
  const visiblePaths = new Set(visible.map((item) => item.path));

  return NAV_SECTIONS.map((section) => {
    const items = visible.filter((item) => placementOf(item).section === section.id);
    const tree = section.groups
      .map((group) => {
        const inGroup = items.filter((item) => placementOf(item).group === group.id);
        const roots = inGroup.filter((item) => {
          const parent = placementOf(item).parent;
          return !parent || !visiblePaths.has(parent);
        });
        return {
          id: group.id,
          label: group.label,
          nodes: roots.map((item) => ({
            item,
            children: items.filter((child) => placementOf(child).parent === item.path),
          })),
        };
      })
      .filter((group) => group.nodes.length > 0);
    return { ...section, tree, items };
  }).filter((section) => section.items.length > 0);
}

/** Yo'l qaysi bo'limga tegishli (zanjir va rels uchun). */
export function sectionOfPath(path: string): NavSection | undefined {
  const item = [...NAV_ITEMS]
    .sort((a, b) => b.path.length - a.path.length)
    .find((entry) => (entry.path === "/" ? path === "/" : path.startsWith(entry.path)));
  if (!item) return undefined;
  const placement = placementOf(item);
  return NAV_SECTIONS.find((section) => section.id === placement.section);
}

/** Zanjir: bo'lim › guruh › ota modul. */
export function trailOf(item: NavItem | undefined): string[] {
  if (!item) return [];
  const placement = placementOf(item);
  const section = NAV_SECTIONS.find((entry) => entry.id === placement.section);
  const trail: string[] = [];
  if (section) trail.push(section.label);
  const group = section?.groups.find((entry) => entry.id === placement.group);
  if (group && group.id !== "other" && group.label !== section?.label) trail.push(group.label);
  if (placement.parent) {
    const parent = NAV_ITEMS.find((entry) => entry.path === placement.parent);
    if (parent) trail.push(parent.title);
  }
  return trail;
}
